import { Component, Input, OnDestroy } from '@angular/core';
import { DwComponent, DwComponentType } from '@devwareapps/devware-cap';
import { concat, of, Subscription } from 'rxjs';
import { catchError, finalize, map, switchMap, take } from 'rxjs/operators';
import { DiscoverableTeam, TeamDiscoveryStateService } from '../../services/team-discovery-state.service';
import { TeamSummaryService } from '../../services/team-summary.service';
import { TeamCardSummary } from '../../models/team-card.models';
import { TeamFormService } from '../../services/team-form.service';

@DwComponent({ key: 'fitness-team-discovery', name: 'Discover teams', componentType: DwComponentType.pageComponent, isGlobal: true })
@Component({ selector: 'app-team-discovery', templateUrl: './team-discovery.component.html',
    styleUrls: ['./team-discovery.component.scss'], providers: [TeamDiscoveryStateService] })
export class TeamDiscoveryComponent implements OnDestroy {
    @Input() embedded = false;
    search = '';
    showAllTeams = true;
    searchVisible = false;
    readonly memberFilterOptions = { displayStyle: 'blockButtons' as const, trueLabel: 'All', falseLabel: 'Accepting Members' };
    limit = 12;
    actionMessage = '';
    creating = false;
    private subscriptions = new Subscription();
    summaries: { [id: number]: TeamCardSummary } = {};
    readonly view$;
    readonly donations$;
    constructor(public state: TeamDiscoveryStateService, summaries: TeamSummaryService, private teamForms: TeamFormService) {
        this.donations$ = state.state$.pipe(switchMap(vm => {
            if (vm.loading || vm.error || !vm.challengeId) { return of({rows:null,loading:vm.loading,error:''}); }
            return concat(of({rows:null,loading:true,error:''}), summaries.loadDonations(vm.challengeId, vm.teams.map(row => row.team)).pipe(
                map(rows => ({rows,loading:false,error:''})),
                catchError(() => of({rows:null,loading:false,error:'Team donations are currently unavailable.'}))));
        }));
        let summaryScope = '';
        this.view$ = state.state$.pipe(switchMap(vm => {
            const scope = JSON.stringify([vm.challengeId, vm.activitiesStarted]);
            const ids = new Set(vm.teams.map(row => row.team.ChallengeTeamId));
            this.summaries = vm.loading || scope !== summaryScope ? {} : Object.keys(this.summaries).reduce((kept, id) => { if (ids.has(Number(id))) { kept[Number(id)] = this.summaries[Number(id)]; } return kept; }, {} as { [id: number]: TeamCardSummary });
            summaryScope = scope;
            if (vm.loading || vm.error || !vm.teams.length) { return of({...vm,summaryLoading:false,summaryError:''}); }
            return concat(of({...vm,summaryLoading:true,summaryError:''}), summaries.load(vm.teams.map(row => row.team.ChallengeTeamId), vm.activitiesStarted).pipe(
                map(data => { this.summaries=data; return {...vm,summaryLoading:false,summaryError:''}; }),
                catchError(() => of({...vm,summaryLoading:false,summaryError:'Team stats are currently unavailable.'}))));
        }));
    }
    filtered(rows: DiscoverableTeam[]): DiscoverableTeam[] {
        const term = this.search.trim().toLocaleLowerCase();
        return rows.filter(row => !row.isMember &&
            (this.showAllTeams || row.team.TeamLocked !== true) &&
            (!term || [row.team.TeamName, row.team.TeamDescription, row.team.TeamLocation]
                .some(value => (value || '').toLocaleLowerCase().includes(term))))
            .sort((a, b) => (a.team.TeamName || '').localeCompare(b.team.TeamName || '') || a.team.ChallengeTeamId - b.team.ChallengeTeamId);
    }
    toggleSearch(): void {
        this.searchVisible = !this.searchVisible;
        if (!this.searchVisible) { this.search = ''; this.showAllTeams = true; this.resetLimit(); }
    }
    myTeams(rows: DiscoverableTeam[]): DiscoverableTeam[] { return rows.filter(row => row.isMember || row.isPending); }
    myIds(rows: DiscoverableTeam[]): number[] { return rows.filter(row => row.isMember).map(row => row.team.ChallengeTeamId); }
    resetLimit(): void { this.limit = 12; this.actionMessage = ''; }
    requestSubmitted(message: string): void { this.actionMessage = message; this.state.refresh(); }
    teamCreated(): void { this.creating=false; this.requestSubmitted('Your team is ready.'); }
    openCreate(challengeId: number): void {
        if (this.creating || !Number.isSafeInteger(challengeId) || challengeId <= 0) { return; }
        this.creating = true;
        this.subscriptions.add(this.teamForms.create(challengeId).pipe(take(1), finalize(() => this.creating = false))
            .subscribe(result => {
                if (!result.cancelled && result.data?.ChallengeTeamId > 0) { this.teamCreated(); }
            }, () => this.actionMessage = 'Unable to open the team form. Please try again.'));
    }
    ngOnDestroy(): void { this.subscriptions.unsubscribe(); }
    trackTeam(index: number, row: DiscoverableTeam): number { return row.team.ChallengeTeamId; }
}
